// The RMS landing strip's model (spec: .claude/features/12928-rms-strip-layouts.md):
// the shapes the server sends and the pure functions the page shapes them
// with. No requests here; Data/strip.ts holds those.

import type { GroupColor, RmsView, RmsViewGroup } from "./views";

/**
 * Which strip a layout and its groups belong to (#11422 round 19, R6): the RMS
 * landing strip (`rms`, every row before #11422), or the documents page's
 * (`intake`), which holds the organization's documents views. Two strips, two
 * sets of groups, never mixed: a view joins only a group of its own strip.
 */
export type StripCollection = "rms" | "intake";

/** The collection every caller that names none means. */
export const DEFAULT_STRIP_COLLECTION: StripCollection = "rms";

export interface StripView {
  view_id: number;
  // null is General.
  group_id: number | null;
  pinned: boolean;
}

export interface StripGroup {
  id: number;
  name: string;
  color: GroupColor;
  // An org group is the whole organization's; a personal one is the user's own.
  scope: "organization" | "personal";
  // The reader's level on the group (#13896): edit or admin may change its
  // name, colour, members and sharing. Absent from a server older than #13896.
  permission?: GroupPermission;
}

export type GroupPermission = "view" | "edit" | "admin";

export interface RmsStrip {
  // General's views first, then each group's, in group order.
  views: StripView[];
  // Every group the user can see, in their order, empty ones included.
  groups: StripGroup[];
  // The user has their own layout; false means they follow the default.
  customized: boolean;
  // The user may set the org default (an RMS manager).
  can_edit_default: boolean;
}

export const EMPTY_STRIP: RmsStrip = {
  views: [],
  groups: [],
  customized: false,
  can_edit_default: false,
};

export type StripOperation =
  | {
      op: "move_view";
      view_id: number;
      // The target section (null is General). Another section than the
      // placement's is a membership change (#13896, requirement 14).
      group_id: number | null;
      // The placement being moved (null is General). Absent, as from a bundle
      // older than #13896, the view leaves every group it sits in.
      from_group_id?: number | null;
      // null, or a view not in the target section, is "at the end".
      before_view_id: number | null;
    }
  | {
      op: "set_pinned";
      view_id: number;
      // The placement to flip (null is General). Absent, as from a bundle
      // older than #13896, every placement of the view flips.
      group_id?: number | null;
      pinned: boolean;
    }
  | { op: "move_group"; group_id: number; before_group_id: number | null };

/**
 * The strip an older backend's bootstrap describes, for the minutes of a
 * deploy when this bundle can reach a server that sends no `strip`: the views
 * in the order sent, each in its `group_id`'s section (General first, then
 * `view_groups` order), pinned unless `user_hidden`; another person's private
 * view and entity-tab or dashboard views are not on the strip. Writes still go
 * through the operations, which every server that serves this bundle has.
 */
export const legacyStrip = (
  views: readonly RmsView[],
  viewGroups: readonly LegacyViewGroup[] = []
): RmsStrip => {
  const groups: StripGroup[] = viewGroups.map((g) => ({
    id: g.id,
    name: g.name,
    color: g.color ?? "blue",
    scope: g.scope ?? "organization",
  }));
  const rank = sectionRank(groups.map((g) => g.id));
  const onStrip = views
    .filter(
      (v) =>
        v.visibility !== "staff_override" &&
        !(v.config as { surface?: string } | undefined)?.surface
    )
    .map(
      (v): StripView => ({
        view_id: v.id,
        group_id: rank.has(v.group_id ?? null) ? (v.group_id ?? null) : null,
        pinned: !v.user_hidden,
      })
    );
  return {
    views: sortSections(
      onStrip,
      groups.map((g) => g.id)
    ),
    groups,
    customized: false,
    can_edit_default: false,
  };
};

/** A group as an older backend's bootstrap lists it. */
export interface LegacyViewGroup {
  id: number;
  name: string;
  color?: GroupColor;
  scope?: StripGroup["scope"];
}

/** The bootstrap's strip, or, from an older backend, the one its views describe. */
export const stripOf = (data: {
  views: readonly RmsView[];
  strip?: RmsStrip;
  view_groups?: readonly LegacyViewGroup[];
}): RmsStrip => data.strip ?? legacyStrip(data.views, data.view_groups);

/**
 * The views in strip order, each carrying its strip group and pin state, then
 * the views the strip does not hold (another person's private view reached as
 * staff) as unpinned and ungrouped. `group_id` and `user_hidden` on a view are
 * read from the strip and nowhere else.
 */
export const joinStrip = (views: RmsView[], strip: RmsStrip): RmsView[] => {
  const byId = new Map(views.map((view) => [view.id, view]));
  const joined: RmsView[] = [];
  const placed = new Set<number>();
  for (const entry of strip.views) {
    const view = byId.get(entry.view_id);
    if (!view) continue;
    placed.add(view.id);
    joined.push({
      ...view,
      group_id: entry.group_id,
      user_hidden: !entry.pinned,
    });
  }
  for (const view of views) {
    if (!placed.has(view.id)) {
      joined.push({ ...view, group_id: null, user_hidden: true });
    }
  }
  return joined;
};

export const stripGroups = (strip: RmsStrip): RmsViewGroup[] =>
  strip.groups.map((group) => ({ ...group }));

/**
 * The strip after `operation`, the way the server's layout applies it
 * (fp/rms/strip_layout.py `apply`). An operation naming a placement or a
 * group the strip does not hold changes nothing, so here a `move_view` to a
 * section the view is not in changes nothing: that is a membership change,
 * which `applyStripOperation` makes first. The parity test holds this to the
 * server's own cases.
 */
export const applyStripLayout = (
  strip: RmsStrip,
  operation: StripOperation
): RmsStrip => {
  const groupOrder = strip.groups.map((g) => g.id);

  if (operation.op === "set_pinned") {
    // Only the flag: a regular pin keeps the view's place (#13052
    // correction). "Pin as default view" moves it, on the server. A request
    // naming no placement flips every placement of the view, as the server
    // reads one from an older bundle.
    const every = operation.group_id === undefined;
    const flips = (v: StripView) =>
      v.view_id === operation.view_id &&
      (every || v.group_id === operation.group_id);
    if (!strip.views.some(flips)) return strip;
    return {
      ...strip,
      views: strip.views.map((v) =>
        flips(v) ? { ...v, pinned: operation.pinned } : v
      ),
    };
  }

  if (operation.op === "move_group") {
    if (!groupOrder.includes(operation.group_id)) return strip;
    const order = groupOrder.filter((g) => g !== operation.group_id);
    const at =
      operation.before_group_id === null
        ? -1
        : order.indexOf(operation.before_group_id);
    order.splice(at === -1 ? order.length : at, 0, operation.group_id);
    const rank = new Map(order.map((g, i) => [g, i]));
    const groups = [...strip.groups].sort(
      (a, b) => (rank.get(a.id) ?? 0) - (rank.get(b.id) ?? 0)
    );
    return { ...strip, groups, views: sortSections(strip.views, order) };
  }

  const target = operation.group_id;
  // From another section: a membership change, not this layout's.
  if (
    operation.from_group_id !== undefined &&
    operation.from_group_id !== target
  )
    return strip;
  const isMoved = (v: StripView) =>
    v.view_id === operation.view_id && v.group_id === target;
  const moved = strip.views.find(isMoved);
  if (!moved) return strip;
  const rest = strip.views.filter((v) => !isMoved(v));
  const before = rest.findIndex(
    (v) => v.view_id === operation.before_view_id && v.group_id === target
  );
  if (before !== -1) {
    rest.splice(before, 0, moved);
  } else {
    // The end of the target section, so sections stay contiguous.
    const rank = sectionRank(groupOrder);
    const end = rest.filter(
      (v) => (rank.get(v.group_id) ?? -1) <= (rank.get(target) ?? -1)
    ).length;
    rest.splice(end, 0, moved);
  }
  return { ...strip, views: rest };
};

/**
 * The strip after `operation`, the way the server answers it (the strip
 * route): a `move_view` to a section the view is not in is a membership
 * change (#13896, requirement 14). The view leaves the section it was dragged
 * from (every group it sits in, when the request names none) and joins the
 * target, then takes its place there as `applyStripLayout` puts it. Used only
 * to show the change at once; the server's answer replaces it.
 */
export const applyStripOperation = (
  strip: RmsStrip,
  operation: StripOperation
): RmsStrip => {
  if (operation.op !== "move_view") return applyStripLayout(strip, operation);
  const change = sectionChange(strip, operation);
  if (change === null) return applyStripLayout(strip, operation);
  // The view now sits in the target: the move is a reorder there.
  return applyStripLayout(moveMembership(strip, operation.view_id, change), {
    ...operation,
    from_group_id: operation.group_id,
  });
};

/**
 * The membership change a `move_view` makes, as the strip route decides it
 * (fp/routes/rms_strip.py `section_change`), or null when it is a reorder
 * within its section or names a placement or group the strip does not hold.
 * Naming a source, the view leaves that group and joins the target unless
 * the target already holds it; naming none (an older bundle), it leaves
 * every group it sits in and joins the target.
 */
const sectionChange = (
  strip: RmsStrip,
  operation: Extract<StripOperation, { op: "move_view" }>
): { leave: number[]; join: number | null } | null => {
  const target = operation.group_id;
  const sections = strip.views
    .filter((v) => v.view_id === operation.view_id)
    .map((v) => v.group_id);
  if (target !== null && !strip.groups.some((g) => g.id === target))
    return null;
  const from = operation.from_group_id;
  if (from === undefined) {
    if (sections.length === 0 || sections.includes(target)) return null;
    return {
      join: target,
      leave: sections.filter((g): g is number => g !== null),
    };
  }
  if (!sections.includes(from) || from === target) return null;
  return {
    join: sections.includes(target) ? null : target,
    leave: from === null ? [] : [from],
  };
};

/**
 * The strip after a view leaves the groups in `leave` and joins `join` (null
 * joins nothing), placed as the server resolves it: a new placement goes at
 * the end of its group, pinned; a view in no group sits at the end of
 * General; a view in a group the reader can see is not in General.
 */
export const moveMembership = (
  strip: RmsStrip,
  viewId: number,
  { leave, join }: { leave: readonly number[]; join: number | null }
): RmsStrip => {
  const own = strip.views.filter((v) => v.view_id === viewId);
  if (own.length === 0) return strip;
  const rank = sectionRank(strip.groups.map((g) => g.id));
  let views = strip.views.filter(
    (v) =>
      v.view_id !== viewId || v.group_id === null || !leave.includes(v.group_id)
  );
  const atEnd = (placement: StripView) => {
    const end = views.filter(
      (v) =>
        (rank.get(v.group_id) ?? -1) <= (rank.get(placement.group_id) ?? -1)
    ).length;
    views = [...views.slice(0, end), placement, ...views.slice(end)];
  };
  if (
    join !== null &&
    rank.has(join) &&
    !views.some((v) => v.view_id === viewId && v.group_id === join)
  ) {
    atEnd({ view_id: viewId, group_id: join, pinned: true });
  }
  if (views.some((v) => v.view_id === viewId && v.group_id !== null)) {
    views = views.filter((v) => v.view_id !== viewId || v.group_id !== null);
  } else if (!views.some((v) => v.view_id === viewId)) {
    atEnd({ view_id: viewId, group_id: null, pinned: own[0].pinned });
  }
  return { ...strip, views };
};

const sectionRank = (groupOrder: number[]): Map<number | null, number> =>
  new Map<number | null, number>([
    [null, -1],
    ...groupOrder.map((g, i): [number, number] => [g, i]),
  ]);

const sortSections = (
  views: StripView[],
  groupOrder: number[]
): StripView[] => {
  const rank = sectionRank(groupOrder);
  return views
    .map((view, index) => ({ view, index }))
    .sort(
      (a, b) =>
        (rank.get(a.view.group_id) ?? -1) - (rank.get(b.view.group_id) ?? -1) ||
        a.index - b.index
    )
    .map(({ view }) => view);
};

// ---- Shaping a strip in the cache ---------------------------------------

/** A view the user just made: pinned, at the end of General, where the
 * server puts it. */
export const placeNewView = (strip: RmsStrip, viewId: number): RmsStrip => {
  if (strip.views.some((v) => v.view_id === viewId)) return strip;
  const general = strip.views.filter((v) => v.group_id === null).length;
  const views = [...strip.views];
  views.splice(general, 0, { view_id: viewId, group_id: null, pinned: true });
  return { ...strip, views };
};

/**
 * The views in strip order for a strip that holds every view of its
 * collection (the documents page's, #11422 R6): a view the strip does not
 * hold yet (one made a moment ago, before the strip is read again) is a pin
 * at the end of General, where the server puts it, never an unpinned view.
 */
export const joinStripPlacingNew = (
  views: RmsView[],
  strip: RmsStrip
): RmsView[] =>
  joinStrip(
    views,
    views.reduce((placed, view) => placeNewView(placed, view.id), strip)
  );

/** The strip with each group passed through `update`. */
export const mapStripGroups = (
  strip: RmsStrip,
  update: (group: StripGroup) => StripGroup
): RmsStrip => ({ ...strip, groups: strip.groups.map(update) });

/** A group the user just made, last, holding `viewIds` (each moved to the
 * end of the new group; a view keeps the other groups it is in). */
export const addStripGroup = (
  strip: RmsStrip,
  group: StripGroup,
  viewIds: number[]
): RmsStrip =>
  setStripGroupMembers(
    { ...strip, groups: [...strip.groups, group] },
    group.id,
    viewIds
  );

/** The strip once group `groupId` holds exactly `viewIds` (#13896): a view
 * that leaves it stays in its other groups, or returns to General; one that
 * joins goes at the end of the group. */
export const setStripGroupMembers = (
  strip: RmsStrip,
  groupId: number,
  viewIds: readonly number[]
): RmsStrip => {
  const leaving = strip.views
    .filter((v) => v.group_id === groupId && !viewIds.includes(v.view_id))
    .map((v) => v.view_id);
  const left = leaving.reduce(
    (current, viewId) =>
      moveMembership(current, viewId, { leave: [groupId], join: null }),
    strip
  );
  return viewIds.reduce(
    (current, viewId) =>
      moveMembership(current, viewId, { leave: [], join: groupId }),
    left
  );
};

/** The placeholder group `from` is now `to`, with its views. */
export const replaceStripGroup = (
  strip: RmsStrip,
  from: number,
  to: StripGroup
): RmsStrip => ({
  ...strip,
  groups: strip.groups.map((g) => (g.id === from ? to : g)),
  views: strip.views.map((v) =>
    v.group_id === from ? { ...v, group_id: to.id } : v
  ),
});

/** A deleted group: its views fall back to General, after General's own, as
 * the server places them. */
export const removeStripGroup = (
  strip: RmsStrip,
  groupId: number
): RmsStrip => {
  const groups = strip.groups.filter((g) => g.id !== groupId);
  // A view that is also in another group stays there and is not in General.
  const elsewhere = new Set(
    strip.views
      .filter((v) => v.group_id !== null && v.group_id !== groupId)
      .map((v) => v.view_id)
  );
  const views = strip.views.flatMap((v) =>
    v.group_id !== groupId
      ? [v]
      : elsewhere.has(v.view_id)
        ? []
        : [{ ...v, group_id: null }]
  );
  return {
    ...strip,
    groups,
    views: sortSections(
      views,
      groups.map((g) => g.id)
    ),
  };
};
